import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import debounce from 'lodash-es/debounce'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'
import autocompleteQuery from '@/queries/autocompleteQuery'

export interface AutocompleteAnalyticsData {
  suggestions: Array<{ suggestion: string }>
  isSuggestionSelected: boolean
  autocompleteQueryValue: string
  search_autocomplete: string[] // List of autocomplete terms shown
}

interface UseSearchAutocompleteOptions {
  onSelectSuggestion: (
    suggestion: string,
    analyticsData: AutocompleteAnalyticsData
  ) => void
  initialQuery?: string
  enabled?: boolean // Optional override for nested flag scenarios
}

export const useSearchAutocomplete = ({
  onSelectSuggestion,
  initialQuery = '',
  enabled,
}: UseSearchAutocompleteOptions) => {
  const flagEnabled = useFlag(FeatureFlags.ENABLE_SEARCH_AUTO_COMPLETE_BOX)
  // Use provided enabled override, or fall back to flag check
  const isAutocompleteEnabled = enabled !== undefined ? enabled : flagEnabled

  const [autocompleteQueryValue, setAutocompleteQueryValue] =
    useState(initialQuery)
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
  const [_isSuggestionSelected, setIsSuggestionSelected] = useState(false)
  const isSuggestionSelectedRef = useRef(false)

  // Query autocomplete suggestions
  const { data: autocompleteData, isFetching } = autocompleteQuery.useWithOpts(
    [{ query: autocompleteQueryValue }],
    { enabled: isAutocompleteEnabled && autocompleteQueryValue.length >= 3 }
  )

  const suggestions = useMemo(
    () => autocompleteData?.querySuggestions || [],
    [autocompleteData]
  )

  // Debounced query trigger
  const debouncedSetAutocompleteQuery = useMemo(
    () =>
      debounce((value: string) => {
        setAutocompleteQueryValue(value)
      }, 300),
    []
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetAutocompleteQuery.cancel()
    }
  }, [debouncedSetAutocompleteQuery])

  const handleInputChange = useCallback(
    (value: string) => {
      if (!isAutocompleteEnabled) return

      setIsSuggestionSelected(false)
      isSuggestionSelectedRef.current = false
      debouncedSetAutocompleteQuery(value)
      if (value.length >= 3) {
        setIsAutocompleteOpen(true)
        setSelectedSuggestionIndex(-1)
      } else {
        setIsAutocompleteOpen(false)
      }
    },
    [isAutocompleteEnabled, debouncedSetAutocompleteQuery]
  )

  const handleSelectSuggestion = useCallback(
    (suggestion: string) => {
      setIsAutocompleteOpen(false)
      setSelectedSuggestionIndex(-1)
      setIsSuggestionSelected(true)
      isSuggestionSelectedRef.current = true
      onSelectSuggestion(suggestion, {
        suggestions,
        isSuggestionSelected: true,
        autocompleteQueryValue,
        search_autocomplete: suggestions.map((s) => s.suggestion),
      })
    },
    [onSelectSuggestion, suggestions, autocompleteQueryValue]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent) => {
      if (!isAutocompleteEnabled || !isAutocompleteOpen) {
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedSuggestionIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          if (selectedSuggestionIndex >= 0) {
            e.preventDefault()
            const selectedSuggestion =
              suggestions[selectedSuggestionIndex]?.suggestion
            if (selectedSuggestion !== undefined) {
              handleSelectSuggestion(selectedSuggestion)
            }
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsAutocompleteOpen(false)
          setSelectedSuggestionIndex(-1)
          break
      }
    },
    [
      isAutocompleteEnabled,
      isAutocompleteOpen,
      suggestions,
      selectedSuggestionIndex,
      handleSelectSuggestion,
    ]
  )

  const handleFocus = useCallback(
    (currentValue: string) => {
      if (
        isAutocompleteEnabled &&
        currentValue.length >= 3 &&
        suggestions.length > 0
      ) {
        setIsAutocompleteOpen(true)
      }
    },
    [isAutocompleteEnabled, suggestions.length]
  )

  const closeAutocomplete = useCallback(() => {
    setIsAutocompleteOpen(false)
    setSelectedSuggestionIndex(-1)
  }, [])

  const clearAutocomplete = useCallback(() => {
    setIsAutocompleteOpen(false)
    setAutocompleteQueryValue('')
    setSelectedSuggestionIndex(-1)
    setIsSuggestionSelected(false)
    isSuggestionSelectedRef.current = false
  }, [])

  return useMemo(
    () => ({
      isAutocompleteEnabled,
      suggestions,
      isAutocompleteOpen,
      selectedSuggestionIndex,
      autocompleteQueryValue,
      isFetching,
      handleInputChange,
      handleSelectSuggestion,
      handleKeyDown,
      handleFocus,
      closeAutocomplete,
      clearAutocomplete,
    }),
    [
      isAutocompleteEnabled,
      suggestions,
      isAutocompleteOpen,
      selectedSuggestionIndex,
      autocompleteQueryValue,
      isFetching,
      handleInputChange,
      handleSelectSuggestion,
      handleKeyDown,
      handleFocus,
      closeAutocomplete,
      clearAutocomplete,
    ]
  )
}
